<script setup lang="ts">
import { A11y } from 'swiper/modules'
import SwiperCore from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { CarouselNavigationType } from '~/helpers/datalayer/types'
import { SwiperOptions } from 'swiper/types'
import type { RichTextPanelProps } from '~/components/cms/RichTextPanel/RichTextPanel.vue'

export type RecommendationRotatorProps = {
  recommenderHeading?: string
  recommenderID: string
  itemIds?: string[]
  supportingData?: {
    home?: {
      action?: {
        value?: 'addToBag'
      }
    }
  }
  recommenderCta?: {
    fields?: Pick<RichTextPanelProps, 'content'>
  }
  preset?: 'column-panel' | 'plp'
  customSwiperOptions?: SwiperOptions
  /** `sizes` for the tiles when customSwiperOptions changes the slide width; see productImageSizes. */
  imageSizes?: string
}

const props = defineProps<RecommendationRotatorProps>()
const carouselImageSizes = props.imageSizes ?? productImageSizes.carousel

const emit = defineEmits<{ (e: 'load', productsCount: number): void }>()

const swiperOptions: SwiperOptions = {
  modules: [A11y],
  slidesPerView: 'auto',
  spaceBetween: 4,
  centerInsufficientSlides: true,
  breakpoints: {
    768: {
      slidesPerView: props.preset === 'column-panel' ? 3 : 4,
      slidesPerGroup: props.preset === 'column-panel' ? 3 : 4,
    },
  },
  ...props.customSwiperOptions,
}
const i18n = useI18n()
const { getRecommendationRotator } = useConstructor()
const { trackRecommendationView } = useConstructorTracker()
const { isLoggedIn } = useUserStore()
const recommendationRotator = ref<RecommendationRotator>()

const loading = ref(true)

const PLP_MAX = 16
const PLP_INITIAL = 8
const showAll = ref(false)
const visibleProducts = computed(() => {
  const products = recommendationRotator.value?.products?.slice(0, PLP_MAX) ?? []
  return showAll.value ? products : products.slice(0, PLP_INITIAL)
})

const swiperUtils = ref<SwiperCore | null>(null)
const isPrevDisabled = computed(() => swiperUtils.value?.isBeginning)
const isNextDisabled = computed(() => swiperUtils.value?.isEnd)
const showArrows = computed(() => {
  if (!swiperUtils.value) {
    // prevents flickering buttons in UI
    return false
  }
  return !(isPrevDisabled.value && isNextDisabled.value)
})
const swiperGaData = ref<{ type?: 'arrow' | 'swipe'; direction?: 'next' | 'prev' }>({})

const onSwiper = (swiper: SwiperCore) => {
  swiperUtils.value = swiper
}

const onSlideChange = (swiper: SwiperCore) => {
  const type = swiperGaData.value.type ?? 'swipe'
  const direction = swiperGaData.value.direction
  swiperGaData.value = {}
  sendProductCarouselDataLayerEvent(swiper, type, direction)
}

const sendProductCarouselDataLayerEvent = (
  swiper: SwiperCore,
  type: CarouselNavigationType,
  direction?: 'next' | 'prev',
) => {
  pushEventToDataLayer(
    'custom.carousel.navigate',
    {
      group: props.recommenderHeading ?? recommendationRotator.value?.heading,
      type: type,
      direction: direction ?? swiper?.swipeDirection,
      info: {
        title: recommendationRotator.value?.products?.[swiper.activeIndex]?.name,
        position: `${swiper.activeIndex}`,
        count: swiper.slides.length,
      },
    },
    i18n.locale.value,
  )
}

const navigateSwiper = (direction: 'next' | 'prev') => {
  if (!swiperUtils.value) {
    return
  }
  if (direction === 'next') {
    swiperGaData.value = { type: 'arrow', direction: 'next' }
    swiperUtils.value.slideNext()
  } else {
    swiperGaData.value = { type: 'arrow', direction: 'prev' }
    swiperUtils.value.slidePrev()
  }
}

onMounted(async () => {
  recommendationRotator.value = await getRecommendationRotator(
    props.recommenderID,
    props?.itemIds,
    props.preset === 'plp' ? PLP_MAX : undefined,
  )
  loading.value = false
  emit('load', recommendationRotator?.value?.products?.length || 0)

  if (trackRecommendationView && recommendationRotator.value) {
    trackRecommendationView({
      url: window.location.href,
      podId: props.recommenderID,
      numResultsViewed: recommendationRotator.value.products?.length || 0,
      items: mapConstructorItemIdFromSearch(recommendationRotator.value.products || []),
      resultCount: recommendationRotator.value.totalProducts,
      resultId: recommendationRotator.value.resultId,
    })
  }
})
</script>

<template>
  <div
    v-if="(loading || recommendationRotator?.products?.length) && props.preset !== 'plp'"
    class="recommender"
    data-testid="recommender"
    :class="{ 'recommender--column-panel': props.preset === 'column-panel' }"
  >
    <div class="recommender__top-section">
      <h2 class="recommender__title">{{ recommenderHeading ?? recommendationRotator?.heading }}</h2>
      <RichTextPanel v-if="recommenderCta?.fields?.content" :content="recommenderCta.fields.content" />
      <div v-if="showArrows" class="recommender__arrow-container">
        <button
          class="recommender__arrow-carousel-left"
          @click="navigateSwiper('prev')"
          :disabled="isPrevDisabled"
          :aria-label="$t('articleSwiper.button.previous')"
        >
          <svg-icon name="mhj:arrow-carousel-left" />
        </button>
        <button
          class="recommender__arrow-carousel-right"
          @click="navigateSwiper('next')"
          :disabled="isNextDisabled"
          :aria-label="$t('articleSwiper.button.next')"
        >
          <svg-icon name="mhj:arrow-carousel-right" />
        </button>
      </div>
    </div>
    <div v-if="loading" class="recommender__skeleton-loader">
      <ProductTileSkeleton v-for="n in 4" :key="n" />
    </div>
    <!-- @vue-skip -->
    <Swiper
      @swiper="onSwiper"
      @slideChange="onSlideChange"
      @tap="(swiper) => sendProductCarouselDataLayerEvent(swiper, 'thumb')"
      v-bind="swiperOptions"
    >
      <SwiperSlide
        v-for="(product, index) in recommendationRotator?.products"
        :key="index"
        class="recommender__swiper-slide"
      >
        <ProductTile
          v-bind="product"
          :list-name="recommendationRotator?.heading"
          :index="index"
          :total-products="recommendationRotator?.totalProducts"
          :open-in-new-tab="false"
          :is-authenticated="isLoggedIn"
          :action="supportingData?.home?.action?.value"
          :is-recommendation="true"
          :recommenderID
          :constructorResultId="recommendationRotator?.resultId"
          :image-sizes="carouselImageSizes"
        />
      </SwiperSlide>
    </Swiper>
  </div>
  <div
    v-else-if="props.preset === 'plp' && (loading || recommendationRotator?.products?.length)"
    class="recommender recommender--plp"
  >
    <h2 class="recommender__title">{{ recommenderHeading ?? recommendationRotator?.heading }}</h2>
    <div v-if="loading" class="recommender__skeleton-loader">
      <ProductTileSkeleton v-for="n in PLP_INITIAL" :key="n" />
    </div>
    <div v-else class="recommender__plp-grid">
      <ProductTile
        v-for="(product, index) in visibleProducts"
        :key="index"
        v-bind="product"
        :list-name="recommendationRotator?.heading"
        :index="index"
        :total-products="recommendationRotator?.totalProducts"
        :open-in-new-tab="false"
        :is-authenticated="isLoggedIn"
        :action="supportingData?.home?.action?.value"
        :is-recommendation="true"
        :recommenderID
        :constructorResultId="recommendationRotator?.resultId"
        :image-sizes="productImageSizes.recommendationGrid"
      />
    </div>
    <div v-if="!showAll" class="recommender__plp-footer">
      <Button variant="primary" @click="showAll = true">
        {{ $t('recommender.view_more') }}
      </Button>
    </div>
  </div>
</template>

<style lang="postcss">
@import '~/assets/css/placeholders.css';

.recommender.recommender--column-panel {
  align-content: center;
  height: 100%;
  margin-bottom: 0;
  padding-top: var(--space-50);
  width: 100%;
  max-width: 100%;
  @media (--desktop) {
    padding-top: 0;
    padding-left: var(--space-70);
    padding-right: var(--space-70);
  }
}

.recommender {
  max-width: var(--container-max-width);
  margin-left: auto;
  margin-right: auto;
  margin-bottom: var(--component-space);
  padding-left: var(--space-50);
  padding-right: var(--space-50);

  .recommender__top-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: var(--space-90);
    gap: 0;

    @media (--mobile-only) {
      flex-direction: column;
      align-items: flex-start;
      margin-bottom: var(--space-70);
    }

    @media (--tablet) {
      flex-direction: column;
      align-items: center;
    }

    & .recommender__title {
      margin-bottom: var(--space-60);
      &:not(:has(+ .action-wrapper, + .rich-text-panel)) {
        margin-bottom: 0;
      }
    }
    &:has(.recommender__arrow-container) {
      .rich-text-panel {
        @media (--mobile-only) {
          padding-right: var(--space-120);
          padding-left: 0;
        }
      }
    }

    & .rich-text-panel {
      margin-bottom: var(--space-50) /* 16px */;
      &:not(:has(+ .action-wrapper)) {
        margin-bottom: 0;
      }
      .rich-text {
        /* Fix for rich text automatically added empty paragraph */
        p:last-of-type:empty {
          display: none;
          margin: 0;
          padding: 0;
        }
      }
    }

    & .action-wrapper {
      margin-bottom: 0;
      @media (--mobile-only) {
        margin-bottom: calc(var(--space-70) * -1);
      }
    }

    @media (--tablet) {
      .rich-text {
        text-align: center;
      }
    }

    &:has(.recommender__title) {
      .recommender__arrow-container {
        @media (--tablet) {
          margin-bottom: calc(var(--space-70) * -1); /* -24px */
        }
        @media (--mobile-only) {
          margin-bottom: calc(var(--space-50) * -1); /* -16px */
        }
      }
    }
  }

  &:not(:has(.recommender--column-panel)) {
    .rich-text {
      .action-wrapper .action {
        margin-top: 0;
        margin-bottom: 0;
        &.link-text.anchor.action {
          text-underline-offset: unset;
          @media (--tablet) {
            display: unset;
          }
        }
      }
    }

    .container {
      @media (--mobile-only) {
        padding-inline: 0;
      }
    }
  }

  .recommender__title {
    @extend %heading-2;
    margin-bottom: 0;
  }

  .recommender__arrow-container {
    min-width: fit-content;
    display: flex;
    flex-wrap: nowrap;
    gap: var(--space-50);
    align-self: flex-end;
  }

  .recommender__description {
    text-align: center;
  }
  .recommender__skeleton-loader {
    width: 100%;
    display: flex;
    gap: var(--space-20);
    flex-wrap: nowrap;
    overflow-x: hidden;
    .skeleton {
      min-width: 9.5rem;
    }
    @media (--tablet) {
      display: grid;
      gap: var(--space-20);
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .swiper-wrapper {
    align-items: stretch;
  }

  .recommender__swiper-slide {
    /* this is required for `slidesPerView: 'auto'` to work */
    width: 9.5rem;
    height: auto;
  }

  .recommender__arrow-carousel-right,
  .recommender__arrow-carousel-left {
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    &:disabled {
      fill: var(--ui-dark-10-t40);
      color: var(--ui-dark-10-t40);
      cursor: default;
    }
    &:hover:not(:disabled) {
      background-color: var(--surface-light-20);
    }
  }
}

.recommender--plp {
  .recommender__title {
    text-align: center;
    margin-bottom: var(--space-90);
  }

  .recommender__plp-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-20);
    margin-bottom: var(--space-60);

    @media (--desktop) {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .recommender__plp-footer {
    display: flex;
    justify-content: center;
  }
}
</style>
